<template>
  <div>
    <ProductFeedSkeleton v-if="pending" />
    <template v-else>
      <div
        class="hidden grid-cols-2 overflow-hidden px-40 pb-40 pt-16 md:grid lg:grid-cols-5 min-[1536px]:grid-cols-6 [.mood-inverse_&]:gap-x-4"
      >
        <ErrorOwners
          v-for="(product, index) in visibleProducts"
          :key="product.id"
          :owners="['bot-squad-search-and-recommendation-front']"
        >
          <ProductCardContent
            class="rounded-lg cursor-pointer hover:shadow-long mood-main bg-surface-default-low"
            :options="{
              withStartingFrom: true,
              withReservedTagSpace: true,
            }"
            :product="mapToProductCardContentProduct(product)"
            @link-click="handleProductClick(product, index)"
          />
        </ErrorOwners>
      </div>
      <div class="flex flex-col md:hidden">
        <div v-for="(product, index) in visibleProducts" :key="product.id">
          <RevButtonBase
            class="rounded-sm flex grow items-center gap-8 p-8 text-left hover:bg-static-default-min-hover text-action-default-hi"
            :to="resolveProductPageLink(product)"
            :underlined="false"
            @click="handleProductClick(product, index)"
          >
            <img alt="" class="rounded-sm h-24" :src="product.image" />
            <div class="flex grow flex-col">
              <span class="body-1-bold">
                {{ product.title }}
              </span>
              <span class="caption">
                {{ i18n(translations.from) }}
                <span class="caption-bold">{{
                  i18n.price(product.price)
                }}</span>
                <span
                  v-if="product.priceNew"
                  class="text-static-info-hi-disabled ml-3"
                >
                  <s>{{ i18n.price(product.priceNew) }}</s>
                  {{ i18n(translations.new) }}
                </span>
              </span>
            </div>
            <IconChevronRight class="shrink-0" />
          </RevButtonBase>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useRouteLocationWithLocale } from '#imports'

import ProductCardContent from '@backmarket/nuxt-layer-recommendation/ProductCardContent.vue'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import ErrorOwners from '@backmarket/nuxt-module-logger/components/ErrorOwners.vue'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { IconChevronRight } from '@ds/icons/IconChevronRight'

import { PRODUCT as PRODUCT_ROUTES } from '~/scopes/product-core/route-names'

import { useVisibleProducts } from '../../composables/useVisibleProducts/useVisibleProducts'

import translations from './ProductFeed.translations'
import ProductFeedSkeleton from './components/ProductFeedSkeleton/ProductFeedSkeleton.vue'

const props = defineProps<{
  products: Product[]
  cnstrcSection?: string
  pending: boolean
}>()

const emits = defineEmits(['click-product'])

const i18n = useI18n()

const resolveLocalizedRoute = useRouteLocationWithLocale()

const { visibleProducts } = useVisibleProducts(() => props.products)

function handleProductClick(product: Product, index: number) {
  emits('click-product', { product, index })
}

function mapToProductCardContentProduct(product: Product) {
  const url = resolveProductPageLink(product)

  return {
    url,
    imageSrc: product.image,
    colorSwatches: product.specifications?.color ?? [],
    title: product.title,
    description: product.description,
    price: product.price,
    discountedPrice: product.discountedPrice,
    priceNew: product.priceNew,
    reviewRating: product.reviewRating,
    listingId: product.id,
    listingPublicId: product.listingPublicId ?? '',
    rawProduct: product,
    deals: product.deals,
    marketingTags: product.marketingTags,
  }
}

function resolveProductPageLink(product: Product) {
  const isMasterPPLink =
    product.productPageLink.name === PRODUCT_ROUTES.MASTER_PP

  return isMasterPPLink
    ? resolveLocalizedRoute({
        name: PRODUCT_ROUTES.MASTER_PP,
        params: {
          slug: product.productPageLink.params.slug,
          id: product.id,
        },
      })
    : resolveLocalizedRoute({
        name: PRODUCT_ROUTES.HOME,
        params: {
          slugV2: product.productPageLink.params.slug,
          uuid: product.id,
        },
      })
}
</script>
